import dayjs, { type Dayjs } from "dayjs";
import dayJsDe from "dayjs/locale/de";
import dayJsEnGb from "dayjs/locale/en-gb";
import i18n from "i18next";

/**
 * date format types.
 */
export const DateFormat = {
  short: "short",
  fullYear: "fullYear",
  withDayName: "withDayName",
  verbose: "verbose",
} as const;
export type DateFormatType = "short" | "fullYear" | "withDayName" | "verbose";

export const languageMapForDayJs = {
  en: dayJsEnGb,
  de: dayJsDe,
} as const;

const getDateFormat = (format: DateFormatType | string) => {
  // Get date formats from i18n resources
  const dateFormats = i18n.t(`dayjsDateFormats`, {
    returnObjects: true,
  }) as Record<string, string>;

  return dateFormats[format] ?? format;
};

/**
 * Formats a date into the given format based on the current language.
 *
 * @param format - The type of date format (DateFormatType) or a date pattern @see https://day.js.org/docs/en/display/format.
 * @param date - The date to be formatted (as a dayjs object or string).
 */
export const formatDate = (
  format: DateFormatType | (string & NonNullable<unknown>),
  date?: Dayjs | string | null
) => {
  if (!date) return "";
  return dayjs.utc(date).format(getDateFormat(format));
};

/**
 * Formats a date and time into a string.
 *
 * @param date - The date to be formatted (as a string).
 * @returns
 */
export function formatDateTime(date: string): string {
  const dateToConvert = new Date(parseInt(date, 10));

  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  } as const;

  return dateToConvert.toLocaleDateString(i18n.language, options);
}
